// frontend/src/services/api.js - FIXED: No localhost fallback
import axios from 'axios';
import { toast } from 'react-hot-toast';

// ============================================================================
// BASE CONFIGURATION
// ============================================================================
const getBaseURL = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    console.error('❌ CRITICAL: VITE_API_URL is not defined!');
    toast.error('Configuration error: API URL not set');
    throw new Error('VITE_API_URL environment variable is required');
  }
  const cleanUrl = apiUrl.replace(/\/api\/?$/, '');
  console.log('✅ API Base URL:', cleanUrl);
  return cleanUrl;
};

const baseURL = getBaseURL();

// Create axios instance
const api = axios.create({
  baseURL: `${baseURL}/api`,
  timeout: 8000,
  headers: {
    'Content-Type': 'application/json',
    'X-Platform': 'web',
  },
  withCredentials: true,
});

// Log all outgoing requests in development
if (import.meta.env.DEV) {
  api.interceptors.request.use((config) => {
    console.log('🌐 API Request:', {
      method: config.method.toUpperCase(),
      url: config.url,
      fullUrl: `${config.baseURL}${config.url}`,
      data: config.data
    });
    return config;
  });
}

// Interceptor to handle file upload timeouts
api.interceptors.request.use((config) => {
  if (config.headers['Content-Type'] === 'multipart/form-data') {
    config.timeout = 60000; // 60 seconds for uploads
  }
  return config;
});

// ============================================================================
// AUTH TOKEN HELPERS
// ============================================================================
const getAuthToken = () => {
  return localStorage.getItem('gigship_token') || localStorage.getItem('token');
};

export const getUser = () => {
  try {
    const user = localStorage.getItem('gigship_user');
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

export const logout = () => {
  localStorage.removeItem('gigship_token');
  localStorage.removeItem('gigship_user');
  localStorage.removeItem('token');
  window.location.href = '/login';
};

// ============================================================================
// REQUEST INTERCEPTOR — attach token to every request
// ============================================================================
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================
api.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      console.log('✅ API Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data
      });
    }
    return response;
  },
  (error) => {
    const originalRequest = error.config;

    // Enhanced error logging
    console.error('❌ API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
    });

    // ✅ FIX: Only hard-logout on 401 for login/auth routes.
    // For ALL other routes (e.g. gig updates), let the error
    // bubble up to the caller so the component can handle it.
    // AuthContext's own interceptor handles session expiry globally.
    // This prevents the double-toast + race condition logout.
    if (error.response?.status === 401) {
      const url = originalRequest?.url || '';
      const isAuthRoute =
        url.includes('/login') ||
        url.includes('/auth/') ||
        url.includes('/request-password-reset') ||
        url.includes('/reset-password');

      if (!isAuthRoute && !originalRequest?._retry) {
        // ✅ Mark as retried so we don't loop
        originalRequest._retry = true;
        // Log but do NOT toast or redirect here —
        // AuthContext interceptor will handle the UX
        console.warn('🔒 401 on protected route:', url, '— deferring to AuthContext handler');
      }
      // Always reject so the caller / AuthContext can handle it
      return Promise.reject(error);
    }

    // Network errors
    if (error.message === 'Network Error') {
      console.error('Network error - check if backend is running at:', baseURL);
      toast.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// FILE HANDLING
// ============================================================================
export const uploadFile = async (file, endpoint) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(endpoint, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadMultipleFiles = async (files, endpoint, folder = 'general') => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });
  formData.append('folder', folder);
  const response = await api.post(endpoint, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const downloadFile = async (url, filename) => {
  try {
    const response = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    toast.error('Failed to download file');
    throw error;
  }
};

// ============================================================================
// API SERVICE CLASS
// ============================================================================
class ApiService {
  constructor() {
    this.api = api;
    this.token = getAuthToken();
  }

  async request(endpoint, options = {}) {
    try {
      const config = { ...options, url: endpoint };
      const response = await this.api.request(config);
      return response.data;
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  async createOrderFromGig(orderData) {
    return this.request('/orders/from-gig', { method: 'POST', data: orderData });
  }

  async getOrder(orderId) {
    return this.request(`/orders/${orderId}`);
  }

  async getUserOrders(params = {}) {
    return this.request('/orders', { params });
  }

  async processOrderPayment(orderId, paymentData) {
    return this.request(`/orders/${orderId}/payment`, { method: 'POST', data: paymentData });
  }

  async submitWork(orderId, workData, files = []) {
    const formData = new FormData();
    formData.append('deliveryNote', workData.deliveryNote);
    files.forEach((file) => formData.append('files', file));
    return this.request(`/orders/${orderId}/submit-work`, {
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async getOrCreateConversation(participantId, orderId = null) {
    return this.request('/conversations', { method: 'POST', data: { participantId, orderId } });
  }

  async getUserConversations(params = {}) {
    return this.request('/conversations', { params });
  }

  async getConversationMessages(conversationId, params = {}) {
    return this.request(`/conversations/${conversationId}/messages`, { params });
  }

  async sendMessage(conversationId, messageData, files = []) {
    const formData = new FormData();
    formData.append('content', messageData.content);
    formData.append('messageType', messageData.messageType || 'text');
    if (messageData.replyTo) formData.append('replyTo', messageData.replyTo);
    files.forEach((file) => formData.append('files', file));
    return this.request(`/conversations/${conversationId}/messages`, {
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async getNotifications(params = {}) {
    return this.request('/notifications', { params });
  }

  async markNotificationAsRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, { method: 'PATCH' });
  }

  async initializePayment(paymentData) {
    return this.request('/payments/initialize', { method: 'POST', data: paymentData });
  }

  async verifyPayment(reference, method = 'auto') {
    return this.request('/payments/verify', { method: 'POST', data: { reference, method } });
  }

  async getWalletBalance() {
    return this.request('/payments/wallet/balance');
  }
}

// ============================================================================
// EXPORTS
// ============================================================================
export const apiService = new ApiService();
export default api;